import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import "./Invitation.css";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? ""
    : "https://krushiapp.dindoripranit.org";

// Stall No Parser
const parseStallNo = (stallNo = "") => {
  const dome = stallNo.match(/[A-Za-z]+/)?.[0] || "";
  const numbers = stallNo.match(/\d+([,-]\d+)*/)?.[0] || "";

  return {
    domeNo: dome,
    stallNumbers: numbers.replace("-", " to "),
  };
};

const Invitation = () => {
  const { stallNo } = useParams();
  const navigate = useNavigate();
  const [stall, setStall] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/v1/stall/list`).then((res) => {
      const found = res.data?.value?.find((s) => s.stallNo === stallNo);
      setStall(found);
    });
  }, [stallNo]);

  useEffect(() => {
    // Preload high-res background image
    const img = new Image();
    img.src = "/assets/images/stall_invitation_page.jpg";
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true);
  }, []);

  if (!stall) {
    return (
      <div className="invalid-access">
        <h3>Invalid Access</h3>
        <button onClick={() => navigate("/")}>Go Back</button>
      </div>
    );
  }

  const { domeNo, stallNumbers } = parseStallNo(stall.stallNo);

  // ================= DOWNLOAD =================
  const downloadInvitation = async () => {
    setIsGenerating(true);
    const element = document.querySelector(".invitation-background"); // ✅ Capture background container
    const buttons = document.getElementById("invitation-actions");

    buttons.style.display = "none";

    // Wait for layout to settle
    await new Promise(resolve => setTimeout(resolve, 100));

    const scale = Math.max(window.devicePixelRatio * 3, 4);

    try {
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png", 1.0);
      link.download = `Invitation_${stall.stallNo}.png`;
      link.click();
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download invitation. Please try again.");
    }

    buttons.style.display = "flex";
    setIsGenerating(false);
  };

  // ================= SHARE =================
  const shareInvitation = async () => {
    setIsGenerating(true);
    const element = document.querySelector(".invitation-background"); // ✅ Same as download
    const buttons = document.getElementById("invitation-actions");

    buttons.style.display = "none";

    await new Promise(resolve => setTimeout(resolve, 100));

    const scale = Math.max(window.devicePixelRatio * 3, 4);

    try {
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      canvas.toBlob(
        async (blob) => {
          const file = new File([blob], "invitation.png", {
            type: "image/png",
          });

          const message = `Jagatik Krushi Mahotsav 2026\n\n${stall.title}\nDome No: ${domeNo}\nStall No: ${stallNumbers}`;

          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({
                title: "JKM Stall Invitation",
                text: message,
                files: [file],
              });
            } catch (shareError) {
              if (shareError.name !== "AbortError") {
                console.error("Share failed:", shareError);
              }
            }
          } else {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `Invitation_${stall.stallNo}.png`;
            link.click();
          }

          buttons.style.display = "flex";
          setIsGenerating(false);
        },
        "image/png",
        1.0
      );
    } catch (error) {
      console.error("Share failed:", error);
      alert("Failed to share invitation. Please try again.");
      buttons.style.display = "flex";
      setIsGenerating(false);
    }
  };

  return (
    <div className="invitation-container">
      <div className="invitation-box">
        {/* BACKGROUND POSTER */}
        <div className="invitation-background">
          {/* ✅ Background Image */}
          <img
            src="/assets/images/stall_invitation_page.jpg"
            alt="Invitation Background"
            className="background-image"
            crossOrigin="anonymous"
            loading="eager"
          />

          {/* Content Overlay */}
          <div className="invitation-content">
            {/* 1️⃣ LOGO */}
            {stall.logoPhoto && (
              <img
                src={stall.logoPhoto}
                alt="Stall Logo"
                className="stall-logo"
                crossOrigin="anonymous"
              />
            )}

            {/* 2️⃣ STALL TITLE */}
          <h2 className="stall-title" title={stall.title}>
  {stall.title}
</h2>

            {/* 3️⃣ + 4️⃣ DOME + STALL NO */}
            <div className="stall-meta">
              <div className="meta-item">
                <span className="meta-label">Dome No:</span>
                <span className="meta-value">{domeNo}</span>
              </div>

              <div className="meta-divider">|</div>

              <div className="meta-item">
                <span className="meta-label">Stall No:</span>
                <span className="meta-value">{stallNumbers}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        {imageLoaded && (
          <div id="invitation-actions" className="action-buttons">
            <button
              onClick={downloadInvitation}
              className="icon-button"
              disabled={isGenerating}
            >
              <DownloadIcon />
              <span className="button-label">
                {isGenerating ? "Generating..." : "Download"}
              </span>
            </button>

            <button
              onClick={shareInvitation}
              className="icon-button"
              disabled={isGenerating}
            >
              <ShareIcon />
              <span className="button-label">
                {isGenerating ? "Generating..." : "Share"}
              </span>
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {!imageLoaded && (
          <div className="loading-indicator">
            <p>Loading invitation...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invitation;